import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { config } from "../../../config";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useAnalytics } from "../../analytics/analytics";
import { useBrandKit } from "../../brand-kit/hooks/use-brand-kit";
import type { ContentTone } from "../../brand-kit/server/brand-kit-service";
import { SupabaseCustomEmailService } from "../../channels/email/server/supabase-custom-email-service";
import { type ChannelName, isEmailChannel } from "../../channels/types";
import { useCreateDesignHuddleProjectComms } from "../../design-huddle/use-design-huddle-project-create-comms";
import { getPageNumber, imageURL } from "../../design-huddle/utils";
import type { LibrarySegment } from "../../discover/library/types";
import { toImportMoment } from "../../discover/transform";
import { useEmailDesigns } from "../../email-design/hooks/use-email-design";
import { SupabaseMomentService } from "../../moment/server/supabase-moment-service";
import { useGenerateCommsContext } from "../../template-preview/generate-comms-context";

export function useLaunchComms(contentTone: ContentTone | "custom"): {
  savable: boolean;
  launching: boolean;
  launch: (channel: ChannelName) => Promise<void>;
} {
  const { gaEvent } = useAnalytics();
  const navigate = useNavigate();

  const [launching, setLaunching] = useState(false);
  const { branding } = useBrandKit();

  const createProject = useCreateDesignHuddleProjectComms();

  const { data: emailDesigns } = useEmailDesigns();

  const {
    text,
    previewImages: { images, selectedImage },
    tones: { selectedTone },
  } = useGenerateCommsContext();
  const img = images?.[selectedImage];
  const title = images?.find((x) => x !== undefined)?.data?.title ?? "New Communication";
  const subjectLine = images?.find((x) => x !== undefined)?.data?.subjectLine ?? "";
  const selectedBrandToneKey = selectedTone?.summary;

  const unsavable = !images || !img || !img.data;

  const launch = useSupabaseCallback(
    async ({ supabase, account_id }, channel: ChannelName) => {
      if (!img || !img.data) return;

      setLaunching(true);

      gaEvent("content_type_generation", {
        action: "mfai_comms_launch",
        value: img.data.templateCode,
        method: channel ?? "",
      });

      const projectData = await createProject(img.data);
      const projectId = projectData?.project_id ?? null;

      const emailDesignId = emailDesigns?.find((e) => e.is_default)?.id;

      const emailFrom = isEmailChannel(channel)
        ? (await new SupabaseCustomEmailService(supabase).getDefault()).data?.address ||
          config.defaultEmailFrom
        : undefined;

      const { data: account } = await supabase
        .from("account")
        .select("enable_teams")
        .eq("id", account_id)
        .single();

      const _template = {
        type: "moment" as const,
        id: "AI generated",
        slug: "ai",
        title,
        what: "",
        context: "",
        img: {
          template_code: img.data.templateCode,
        },
        content: {
          email_subject: subjectLine,
          professional: text.get("professional")?.data?.text ?? "",
          casual: text.get("casual")?.data?.text ?? "",
          direct: text.get("direct")?.data?.text ?? "",
          custom: (selectedBrandToneKey ? text.get(selectedBrandToneKey)?.data?.text : "") ?? "",
        },
        recommendations: {
          send_time: "Immediately",
          channel: "#general",
        },
        date: null,
        end_date: null,
        trigger: null,
        segment: "" as LibrarySegment,
        trending: null,
        hero: false,
        top: null,
        geo: "International",
        program: {
          color: "#FF747F",
          name: "Other",
          slug: "other",
          photo: "",
        },
      };
      const momentTemplate = toImportMoment(
        _template,
        contentTone,
        emailDesignId,
        projectId
          ? {
              project_id: projectId,
              account_id,
              page_number: getPageNumber(branding),
              image_type: img.data.imageType,
            }
          : undefined,
        channel,
        projectId ? imageURL(account_id, projectId) : undefined,
        emailFrom,
        account?.enable_teams,
      );

      const { data, error } = await new SupabaseMomentService(supabase).importMoment(
        momentTemplate,
      );

      setLaunching(false);
      if (error) toast(error.message);
      else {
        void navigate(`/moments/${data}`);
      }
    },
    [
      img,
      gaEvent,
      createProject,
      emailDesigns,
      title,
      subjectLine,
      text,
      selectedBrandToneKey,
      contentTone,
      branding,
      navigate,
    ],
  );

  return {
    savable: !unsavable,
    launching,
    launch,
  };
}
